import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EyebrowPill } from "@/components/shared/EyebrowPill";
import { SectionHeading } from "@/components/shared/SectionHeading";
import tasMarketNeed from "@/assets/tas-builder-market-need.webp.asset.json";
import trainersMatrix from "@/assets/trainers-matrix.webp.asset.json";
import bulkDocUpload from "@/assets/bulk-document-upload.webp.asset.json";
import documentMgmt from "@/assets/document-management.webp.asset.json";
import tasUnits from "@/assets/tas-builder-units.webp.asset.json";

type Feature = {
  src: string | null;
  title: string;
  caption: string;
  placeholder?: boolean;
};

const features: Feature[] = [
  {
    src: tasMarketNeed.url,
    title: "TAS Quality Engine",
    caption:
      "Validates scope against Training.gov.au, pulls live Labour Market Demand data, scans licensing requirements across every Australian jurisdiction, and maps compliance clauses automatically — producing a TAS built to withstand ASQA scrutiny, not just tick a box.",
  },
  {
    src: trainersMatrix.url,
    title: "Trainers Matrix Engine",
    caption:
      "Every trainer mapped to every unit they are qualified to deliver — with credentials, currency, and compliance classification tracked in real time. 39 trainers, 5 risk flags, currency gaps identified and flagged before your auditor finds them.",
  },
  {
    src: bulkDocUpload.url,
    title: "AI Bulk Document Upload",
    caption:
      "Upload your entire document suite at once — ComplyHub's AI reads each file, extracts metadata, sets review frequency, assigns compliance triggers, and routes documents to the correct repository automatically. 16 documents analyzed and ready to import in minutes, not days.",
  },
  {
    src: documentMgmt.url,
    title: "Document Register",
    caption:
      "76 published documents, every one version-controlled, repository-assigned, and tagged to its compliance section — Governance, Trainers, Students, or Third Parties. Your entire document suite audit-ready and searchable in one place.",
  },
  {
    src: tasUnits.url,
    title: "TAS Quality Engine:\u00a0 Unit Selection",
    caption:
      "Packaging rules imported automatically from Training.gov.au — core units auto-selected, elective requirements clearly displayed, and compliance confirmed before you move to the next phase. No more packaging rule errors in your TAS.",
  },
  {
    src: null,
    title: "29 Integrated Compliance Registers",
    caption:
      "Every compliance register your RTO needs — all connected, all searchable, all feeding into governance. Governance & Risk, Students & Support, Training & Assessment, VET Workforce, and Document Management. Nothing in a spreadsheet. Nothing missing at audit.",
    placeholder: true,
  },
];

const VisualProof = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  return (
    <section className="py-20 md:py-28 bg-ch-lavender text-ch-acai">
      <div className="container">
        <div className="text-center mb-14 animate-fade-up">
          <EyebrowPill variant="onTint">Platform Capabilities</EyebrowPill>
          <SectionHeading className="mb-5">See ComplyHub in Action</SectionHeading>
          <p className="text-lg text-ch-acai/75 max-w-3xl mx-auto">
            Built by RTO compliance specialists who've sat across the table from ASQA auditors — and designed every feature around what they actually ask for.
          </p>
        </div>

        {(() => {
          const renderCard = (feature: Feature, imgHeight: number) => (
            <button
              key={feature.title}
              onClick={() => {
                if (feature.src) setSelectedImage(feature.src);
              }}
              className="group relative rounded-2xl overflow-hidden bg-white shadow-ch-card border border-ch-acai/10 transition-all duration-300 hover:shadow-ch-float hover:-translate-y-1 text-left flex flex-col w-full h-full"
            >
              <div className="w-full overflow-hidden" style={{ borderRadius: "8px 8px 0 0" }}>
                {feature.placeholder ? (
                  <div
                    style={{
                      height: `${imgHeight}px`,
                      background: "linear-gradient(to right, #7130A0, #ED1878)",
                    }}
                    className="w-full flex items-center justify-center"
                  >
                    <span className="font-heading text-white" style={{ fontSize: 20 }}>
                      29 Compliance Registers
                    </span>
                  </div>
                ) : (
                  <img
                    src={feature.src ?? ""}
                    alt={feature.title}
                    style={{ height: `${imgHeight}px` }}
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="p-6 flex-1">
                <h3 className="text-xl font-bold text-ch-purple mb-2 font-heading">
                  {feature.title}
                </h3>
                <p className="text-base text-ch-acai/75 leading-relaxed">
                  {feature.caption}
                </p>
              </div>
            </button>
          );
          return (
              <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-6 gap-8 items-stretch">
                {features.map((f) => (
                  <div key={f.title} className="md:col-span-2">
                    {renderCard(f, 220)}
                  </div>
                ))}
              </div>
          );
        })()}

        <div className="mt-12 flex justify-center">
          <Link to="/demo#book">
            <Button size="lg" variant="chPrimary" className="h-12 px-8">
              Book a Demo
            </Button>
          </Link>
        </div>
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-ch-acai/95 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-ch-cyan transition-colors p-2"
            onClick={() => setSelectedImage(null)}
            aria-label="Close preview"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={selectedImage}
            alt="Product screenshot"
            className="max-w-full max-h-[90vh] rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default VisualProof;
